import { IPMEventPayload, IPostMessageListener, IPMEventResponse } from 'integrations/interfaces';
import SentryRepository from 'repositories/SentryRepository/SentryRepository';
import { formatPayload } from './utils/formatPayload';

interface IPostMessenger {
    emit(message: IPMEventPayload): void;
    subscribe<T = unknown>(type: string, listener: ICallback<IPMEventResponse<T>>): ICallback;
    get<T = unknown, P = unknown>(type: string, payload?: P): Promise<T>;
}

export default class PostMessenger implements IPostMessenger {
    private readonly target = window.opener || window.parent || window.top;

    private listeners = new Map<string, IPostMessageListener[]>();

    private readonly sentry: SentryRepository;

    private validateMessage = (event: MessageEvent): boolean | IPMEventPayload => {
        if (!this.listeners.size || typeof event.data !== 'string') {
            return false;
        }

        try {
            return JSON.parse(event.data);
        } catch (error) {
            this.sentry.error(error as Error, { eventData: event.data });
            return false;
        }
    };

    private messageHandler = (event: MessageEvent): void => {
        const message = this.validateMessage(event) as IPMEventPayload;

        if (!message) {
            return;
        }

        if (!this.listeners.has(message.type)) {
            return;
        }

        const listeners = this.listeners.get(message.type);

        if (!listeners.length) {
            return;
        }

        listeners.forEach((listener) => listener(message));
    };

    constructor(sentryRepository: SentryRepository) {
        this.sentry = sentryRepository;

        window.addEventListener('message', this.messageHandler, false);
    }

    public emit = (message: IPMEventPayload): void => {
        try {
            const payload = formatPayload(message);

            if (!payload) {
                return;
            }

            this.target.postMessage(payload, '*');

            if (window.top !== this.target) {
                window.top.postMessage(payload, '*');
            }
        } catch (error) {
            this.sentry.error(error as Error);
        }
    };

    public subscribe = <T = unknown>(
        type: string,
        listener: IPostMessageListener<T>,
    ): ICallback => {
        this.listeners.set(type, [
            listener as IPostMessageListener,
            ...(this.listeners.get(type) || []),
        ]);

        return () => {
            const typeListeners = this.listeners.get(type);

            typeListeners.splice(typeListeners.indexOf(listener as IPostMessageListener), 1);
        };
    };

    public get = <T = unknown, P = unknown>(type: string, payload?: P): Promise<T> =>
        new Promise((resolve, reject) => {
            let autoRejectTimeout: number = null;

            const unsubscribe = this.subscribe<T>(type, (message) => {
                window.clearTimeout(autoRejectTimeout);
                unsubscribe();
                resolve(message.payload);
            });

            this.emit({
                type,
                payload,
            });

            autoRejectTimeout = window.setTimeout(() => {
                reject();
            }, 1000);
        });
}
